import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertCircle, Copy, Loader2, Plus, Video, X } from "lucide-react";
import { useRef } from "react";
import { cn } from "../lib/utils";
import { UploadItem } from "./MediaUploadSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

interface MediaFilmstripProps {
  items: UploadItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onRemove: (id: string) => void;
  onAdd: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDuplicate?: (id: string) => void;
}

function SortableFilmstripItem({
  item,
  index,
  isActive,
  onSelect,
  onRemove,
  onDuplicate,
}: {
  item: UploadItem;
  index: number;
  isActive: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isVideo = item.type.startsWith("video");

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "relative flex-shrink-0 h-20 w-20 rounded-md border-2 overflow-visible cursor-pointer group transition-all",
        isActive
          ? "border-primary"
          : "border-border hover:border-primary/50"
      )}
      onClick={onSelect}
    >
      <div className="absolute inset-0 rounded-md overflow-hidden">
        {item.error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
        ) : item.uploadProgress !== undefined && item.uploadProgress < 100 ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
          </div>
        ) : (
          <img
            src={item.preview}
            alt={`Slide ${index + 1}`}
            className="h-full w-full object-cover pointer-events-none"
          />
        )}

        {isVideo && (
          <div className="absolute top-1 right-1 bg-black/60 rounded p-0.5">
            <Video className="h-3 w-3 text-white" />
          </div>
        )}

        {/* Index Badge */}
        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
          {index + 1}
        </div>
      </div>

      {/* Duplicate Button (visible on hover) */}
      {onDuplicate && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute -top-2 -left-2 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      )}

      {/* Remove Button (visible on hover) */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <X className="h-3 w-3" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove slide?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove slide {index + 1} from your post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={onRemove}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function MediaFilmstrip({
  items,
  selectedIndex,
  onSelect,
  onReorder,
  onRemove,
  onAdd,
  onDuplicate,
}: MediaFilmstripProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 px-1 min-h-[100px]">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((i) => i.id)}
          strategy={horizontalListSortingStrategy}
        >
          {items.map((item, index) => (
            <SortableFilmstripItem
              key={item.id}
              item={item}
              index={index}
              isActive={index === selectedIndex}
              onSelect={() => onSelect(index)}
              onRemove={() => onRemove(item.id)}
              onDuplicate={onDuplicate ? () => onDuplicate(item.id) : undefined}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Add Button */}
      <div className="flex-shrink-0">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/svg+xml"
          multiple
          onChange={onAdd}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className="h-20 w-20 border-dashed border-2 flex flex-col gap-1 items-center justify-center text-muted-foreground hover:text-foreground"
                onClick={() => fileInputRef.current?.click()}
              >
                <Plus className="h-6 w-6" />
                <span className="text-xs">Add</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Add media</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
